import React, { useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useQuery } from "@apollo/client/react";

import useMutation from "../../hooks/useMutation";
import useAuth from "../../hooks/useAuth";
import { TRANSFER_ASSET_MUTATION } from "../../constants/graphql/mutations";
import ActionModal from "../../components/modals/action-modal";
import { CLIENT_PROJECT_OPTIONS_QUERY } from "../../constants/graphql/queries";
import EntitySelect from "../../components/EntitySelect";
import useNotification from "../../hooks/notification";
import {
  buildDuplicateProjectNameCounts,
  getProjectClientLabel,
  getProjectOptionLabel,
} from "../../utils/projectOptionLabels";
import { getActiveProjectsForClients } from "../../utils/clientProjectOptions";

export default function TransferModal({ asset, handeRefetch, onTransfer, onClose }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const showClientNameInProjectList = Boolean(settings.display_client_name_email_notification);
  const { notifySuccess, notifyError } = useNotification();
  const [projectId, setProjectId] = useState(null);
  const [transferAsset] = useMutation(TRANSFER_ASSET_MUTATION);
  const { data: { clients = [] } = {} } = useQuery(CLIENT_PROJECT_OPTIONS_QUERY);
  const projectList = useMemo(() => getActiveProjectsForClients(clients), [clients]);
  const duplicateNameCounts = useMemo(
    () => buildDuplicateProjectNameCounts(projectList),
    [projectList]
  );

  const handleTransferAsset = useCallback(() => {
    transferAsset({
      variables: {
        assetId: asset?.id,
        projectId,
      },
      onSuccess: () => {
        notifySuccess("Asset successfully transferred");
        if (handeRefetch) {
          handeRefetch();
        }
        if (onTransfer) {
          onTransfer({ asset: asset, projectId });
        }
        onClose();
      },
      onFailure: () => {
        notifyError("Something went wrong");
      },
    });
  }, [asset, transferAsset, onClose, notifyError, notifySuccess]);

  return (
    <ActionModal open title="Transfer Asset" maxWidth={440} onClose={onClose} disableTransition>
      <EntitySelect
        fullWidth
        label={`Select New ${settings.filter_field2}`}
        value={projectId}
        suggestions={projectList}
        getOptionLabel={option =>
          getProjectOptionLabel(
            option,
            duplicateNameCounts,
            showClientNameInProjectList
          )
        }
        renderOption={(props, option) => {
          const optionProps = { ...props };
          delete optionProps.key;
          const clientLabel = getProjectClientLabel(
            option,
            duplicateNameCounts,
            showClientNameInProjectList
          );
          return (
            <li {...optionProps} key={option.id}>
              {option.name}
              {clientLabel ? (
                <Typography component="span" ml={0.5} color="text.secondary" fontSize={14}>
                  {" - "}
                  {clientLabel}
                </Typography>
              ) : null}
            </li>
          );
        }}
        onChange={projectItem => {
          setProjectId(projectItem.id);
        }}
      />

      <Box mt={4} />
      <Grid container spacing={1}>
        <Grid size={6}>
          <Button fullWidth variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Grid>
        <Grid size={6}>
          <Button
            disabled={!projectId}
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleTransferAsset}
          >
            Transfer
          </Button>
        </Grid>
      </Grid>
    </ActionModal>
  );
}
